import type { PostHog, Properties } from "posthog-js"
import { getPostHogEnvironment } from "@/lib/posthog/config"
import { getPostHogClient, postHogState } from "@/lib/posthog/state"
import type { PostHogAuthUser, PostHogClientContext, PostHogOrganisation } from "@/lib/posthog/types"
import { unregisterProperties, unregisterSessionProperties, withoutUndefinedValues } from "@/lib/posthog/utils"

const ORGANISATION_PROPERTY_NAMES = [
  "organisation_id",
  "organisation_key",
  "organisation_name",
  "organisation_slug",
  "organisation_website",
  "workos_organization_id",
] as const

const CLIENT_SESSION_PROPERTY_NAMES = [
  "client_id",
  "client_key",
  "client_name",
] as const

export function syncPostHogClientContext(clientContext: PostHogClientContext | null) {
  const client = getPostHogClient()
  if (!client) {
    return
  }

  postHogState.lastClientContext = clientContext
  if (!clientContext) {
    unregisterSessionProperties(client, CLIENT_SESSION_PROPERTY_NAMES)
    return
  }

  client.register_for_session(getClientContextProperties(clientContext))
  client.group("client", getClientKey(clientContext.id), {
    id: clientContext.id,
    name: clientContext.name,
    environment: getPostHogEnvironment(),
    app: "tlmc",
  })
}

export function syncPostHogOrganisationContext(client: PostHog, organisation: PostHogOrganisation | null) {
  if (!organisation) {
    unregisterProperties(client, ORGANISATION_PROPERTY_NAMES)
    client.resetGroups()
    postHogState.lastOrganisationSignature = ""
    postHogState.lastClientContext = null
    return
  }

  const organisationProperties = getOrganisationContextProperties(organisation)
  client.register(organisationProperties)

  const organisationSignature = JSON.stringify(organisationProperties)
  if (postHogState.lastOrganisationSignature !== organisationSignature) {
    client.group("organisation", getOrganisationKey(organisation.id), {
      id: organisation.id,
      name: organisation.name,
      slug: organisation.slug,
      website: organisation.website,
      workos_organization_id: organisation.workosOrganizationId,
      environment: getPostHogEnvironment(),
      app: "tlmc",
    })
    postHogState.lastOrganisationSignature = organisationSignature
  }
}

export function getPersonProperties(user: PostHogAuthUser) {
  const fullName = getUserDisplayName(user)

  return withoutUndefinedValues({
    email: user.email,
    $email: user.email,
    name: fullName,
    $name: fullName,
    first_name: user.firstName,
    last_name: user.lastName,
    environment: getPostHogEnvironment(),
    app: "tlmc",
  })
}

export function getUserContextProperties(user: PostHogAuthUser) {
  return withoutUndefinedValues({
    user_email: user.email,
    user_first_name: user.firstName,
    user_id: String(user.id),
    user_key: getUserDistinctId(user.id),
    user_last_name: user.lastName,
    user_name: getUserDisplayName(user),
  })
}

export function getClientContextProperties(clientContext: PostHogClientContext | null) {
  if (!clientContext) {
    return {}
  }

  const clientId = String(clientContext.id)
  return withoutUndefinedValues({
    client_id: clientId,
    client_key: getClientKey(clientId),
    client_name: clientContext.name,
  })
}

export function getResolvedClientContext(clientId: string | undefined): PostHogClientContext | null {
  if (!clientId) {
    return null
  }

  if (String(postHogState.lastClientContext?.id) === String(clientId)) {
    return postHogState.lastClientContext
  }

  return { id: clientId }
}

export function resetPostHogContext() {
  postHogState.lastIdentifiedDistinctId = null
  postHogState.lastPersonPropertiesSignature = ""
  postHogState.lastOrganisationSignature = ""
  postHogState.lastAuthSessionLoadedSignature = ""
  postHogState.lastClientContext = null
}

export function getUserDistinctId(userId: number | string) {
  return `tlmc:user:${userId}`
}

export function getOrganisationKey(organisationId: number | string) {
  return `tlmc:organisation:${organisationId}`
}

export function getClientKey(clientId: number | string) {
  return `tlmc:client:${clientId}`
}

function getOrganisationContextProperties(organisation: PostHogOrganisation) {
  return withoutUndefinedValues({
    organisation_id: organisation.id,
    organisation_key: getOrganisationKey(organisation.id),
    organisation_name: organisation.name,
    organisation_slug: organisation.slug,
    organisation_website: organisation.website,
    workos_organization_id: organisation.workosOrganizationId,
  })
}

function getUserDisplayName(user: PostHogAuthUser) {
  return [user.firstName, user.lastName].filter(Boolean).join(" ").trim() || user.email
}

export function appEventProperties(properties: Properties = {}) {
  return withoutUndefinedValues({
    environment: getPostHogEnvironment(),
    app: "tlmc",
    ...properties,
  })
}
